import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MessageCircle, Sparkles, Inbox } from "lucide-react";
import { fetchMyQuestions } from "../lib/api";
import { MESSAGES } from "../lib/testIds";
import QuestionThreadModal from "../components/QuestionThreadModal";

const formatWhen = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const now = new Date();
        const diff = (now - d) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch {
        return "";
    }
};

const MessagesPage = () => {
    const { qid: routeQid } = useParams();
    const navigate = useNavigate();
    const [threads, setThreads] = useState([]);
    const [totalUnread, setTotalUnread] = useState(0);
    const [loading, setLoading] = useState(true);
    const [openQid, setOpenQid] = useState(routeQid || null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const data = await fetchMyQuestions();
            setThreads(data.threads || []);
            setTotalUnread(data.total_unread || 0);
        } catch {
            setThreads([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // If /messages/:qid was hit directly, open the thread modal on mount.
    useEffect(() => {
        if (routeQid) setOpenQid(routeQid);
    }, [routeQid]);

    const handleOpen = (qid) => {
        setOpenQid(qid);
        navigate(`/messages/${qid}`, { replace: false });
    };
    const handleClose = () => {
        setOpenQid(null);
        navigate("/messages", { replace: false });
        load();
    };

    return (
        <main
            data-testid={MESSAGES.page}
            className="relative z-10 mx-auto max-w-3xl px-6 py-14 md:px-10 md:py-20"
        >
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#2e3220]/10 bg-[#F4F1EA] px-3 py-1.5">
                <Sparkles className="h-3.5 w-3.5 text-[#737a3d]" strokeWidth={2.5} />
                <span className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                    Messages
                </span>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
                <h1 className="font-display text-4xl font-bold tracking-tight text-[#2e3220] sm:text-5xl">
                    Your conversations
                </h1>
                {totalUnread > 0 && (
                    <span className="rounded-full bg-[#B56C5A] px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#F4F1EA]">
                        {totalUnread} unread
                    </span>
                )}
            </div>
            <p className="mt-2 text-base text-[#5a6142]">
                Questions about your listings and rentals. Both sides can reply
                here — every message also emails the other neighbor.
            </p>

            <section className="mt-8 rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-4">
                {loading ? (
                    <div className="space-y-2">
                        <div className="h-16 animate-pulse rounded-2xl bg-[#E8E2D0]" />
                        <div className="h-16 animate-pulse rounded-2xl bg-[#E8E2D0]" />
                    </div>
                ) : threads.length === 0 ? (
                    <div
                        data-testid={MESSAGES.empty}
                        className="flex flex-col items-center gap-3 py-14 text-center"
                    >
                        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#737a3d]/10 text-[#737a3d]">
                            <Inbox className="h-6 w-6" strokeWidth={2} />
                        </span>
                        <div>
                            <p className="font-display text-lg font-bold text-[#2e3220]">
                                No conversations yet
                            </p>
                            <p className="mt-1 text-sm text-[#5a6142]">
                                When a neighbor asks about your listing (or you ask
                                about theirs), the thread will land here.
                            </p>
                        </div>
                    </div>
                ) : (
                    <ul
                        data-testid={MESSAGES.list}
                        className="divide-y divide-[#2e3220]/8"
                    >
                        {threads.map((t) => (
                            <li key={t.id}>
                                <button
                                    type="button"
                                    data-testid={MESSAGES.row(t.id)}
                                    onClick={() => handleOpen(t.id)}
                                    className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition-colors hover:bg-[#E8E2D0]/60"
                                >
                                    {t.item_image ? (
                                        <img
                                            src={t.item_image}
                                            alt={t.item_name}
                                            className="h-12 w-12 shrink-0 rounded-xl object-cover"
                                        />
                                    ) : (
                                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#737a3d]/12 text-[#737a3d]">
                                            <MessageCircle
                                                className="h-5 w-5"
                                                strokeWidth={2}
                                            />
                                        </span>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <p className="truncate text-sm font-semibold text-[#2e3220]">
                                                {t.other_name || "Neighbor"}
                                            </p>
                                            <span className="rounded-full bg-[#737a3d]/12 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-[#737a3d]">
                                                {t.your_role === "owner" ? "As lender" : "As renter"}
                                            </span>
                                        </div>
                                        <p className="mt-0.5 truncate text-xs text-[#5a6142]">
                                            {t.item_name} · {t.message_count}{" "}
                                            message{t.message_count === 1 ? "" : "s"}
                                        </p>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <span className="text-[10px] text-[#5a6142]/80">
                                            {formatWhen(t.last_message_at)}
                                        </span>
                                        {t.unread > 0 && (
                                            <span
                                                data-testid={MESSAGES.unreadBadge(t.id)}
                                                className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#B56C5A] px-1.5 text-[10px] font-bold text-[#F4F1EA]"
                                            >
                                                {t.unread}
                                            </span>
                                        )}
                                    </div>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <QuestionThreadModal
                questionId={openQid}
                open={!!openQid}
                onClose={handleClose}
                onChange={load}
            />
        </main>
    );
};

export default MessagesPage;
